import React from 'react';

import clsx from 'clsx';
import { Box, ButtonBase, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { RiPushpin2Fill } from 'react-icons/ri';

import { notificationBadge } from '@vyce/core/src/theme/styles';

import { chatBubbleColors } from './ChatBubble';
import { unreadLabel } from './chatListTime';

export interface ChatListRowProps {
  avatar: React.ReactNode;
  title: string;
  // Before the title, e.g. a group or a site glyph.
  titleIcon?: React.ReactNode;
  // After the title, e.g. the verified mark on Vyce's own chat.
  titleBadge?: React.ReactNode;
  // Already formatted, see chatListTime.
  time?: string;
  preview?: string;
  unread?: number;
  // Kept on top of the list.
  pinned?: boolean;
  // The chat open beside the list.
  selected?: boolean;
  onClick?: () => void;
}

// One chat in the list, the same in every chat list: avatar, name, time, the
// last message and the unread count, Telegram-style.
export const ChatListRow = React.memo<ChatListRowProps>(
  ({ avatar, title, titleIcon, titleBadge, time, preview, unread = 0, pinned, selected, onClick }) => {
    const classes = useStyles();

    return (
      <ButtonBase
        component="div"
        className={clsx(classes.row, { [classes.selected]: selected })}
        onClick={onClick}
        aria-current={selected || undefined}
        aria-label={unread ? `${title}, ${unread} unread` : title}
      >
        <Box className={classes.avatar}>{avatar}</Box>
        <Box className={classes.body}>
          <Box className={classes.line}>
            {titleIcon && <span className={classes.icon}>{titleIcon}</span>}
            <Typography component="span" className={classes.title}>
              {title}
            </Typography>
            {titleBadge}
            {!!time && (
              <Typography
                component="span"
                className={clsx(classes.time, { [classes.timeUnread]: !!unread })}
              >
                {time}
              </Typography>
            )}
          </Box>
          <Box className={classes.line}>
            <Typography component="span" className={classes.preview}>
              {preview || ' '}
            </Typography>
            {unread > 0 ? (
              <span className={classes.unread}>{unreadLabel(unread)}</span>
            ) : (
              pinned && <RiPushpin2Fill size={15} className={classes.pin} aria-label="Pinned" />
            )}
          </Box>
        </Box>
      </ButtonBase>
    );
  }
);

const useStyles = makeStyles((theme: Theme) => {
  const { outBackground, outText } = chatBubbleColors(theme);

  return createStyles({
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      width: '100%',
      minHeight: 72,
      padding: '8px 10px',
      borderRadius: 12,
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'background-color 120ms',
      '&:hover': { backgroundColor: theme.palette.action.hover },
    },
    selected: {
      backgroundColor: outBackground,
      '&:hover': { backgroundColor: outBackground },
      '& $title, & $preview, & $time, & $icon, & $pin': { color: outText },
      '& $preview, & $time': { opacity: 0.85 },
    },
    avatar: { flex: 'none', display: 'flex' },
    body: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
    line: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 },
    icon: { flex: 'none', display: 'flex', color: theme.palette.text.secondary },
    title: {
      flex: '0 1 auto',
      minWidth: 0,
      fontSize: 15,
      fontWeight: 600,
      lineHeight: '20px',
      color: theme.palette.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    time: {
      flex: 'none',
      marginLeft: 'auto',
      paddingLeft: 8,
      fontSize: 12,
      lineHeight: '20px',
      color: theme.palette.text.secondary,
    },
    timeUnread: { color: theme.palette.primary.main },
    preview: {
      flex: 1,
      minWidth: 0,
      fontSize: 14,
      lineHeight: '20px',
      color: theme.palette.text.secondary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    unread: {
      flex: 'none',
      minWidth: 22,
      height: 22,
      padding: '0 7px',
      borderRadius: 11,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '22px',
      textAlign: 'center',
      backgroundColor: notificationBadge.background,
      color: notificationBadge.text,
    },
    pin: { flex: 'none', color: alpha(theme.palette.text.secondary, 0.8) },
  });
});
